import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MobileNavService } from '../../../shared/mobile/mobile-bottom-nav/mobile-nav.service';
import { MobilePageShellComponent } from '../../../shared/mobile/mobile-page-shell/mobile-page-shell.component';
import { MobileDataCardComponent, MobileCardField } from '../../../shared/mobile/mobile-data-card/mobile-data-card.component';
import {
  VariableOverheadItem,
  FixedOverheadItem,
  Salary,
} from '../business-form.component';
import { MultiSelectDropdownComponent, Option } from '../multi-select-dropdown.component';
import { SingleSelectDropdownComponent } from '../single-select-dropdown.component';
import { BusinessItemOffcanvasComponent, BusinessOffcanvasMode } from './business-item-offcanvas/business-item-offcanvas.component';
import { SectionCardComponent } from '../../../shared/section-card/section-card.component';

interface Employee {
  employeeID: number | null;
  firstName: string;
  lastName: string;
  contactInfo: string | null;
  hireDate: string;
  roleID: number | null;
  roleDescription?: string;
  isActive: boolean;
}

interface Role {
  roleID: number | null;
  roleName: string;
  description: string | null;
}

@Component({
  selector: 'app-mobile-business-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MobilePageShellComponent,
    MobileDataCardComponent,
    MultiSelectDropdownComponent,
    SingleSelectDropdownComponent,
    BusinessItemOffcanvasComponent,
    SectionCardComponent,
  ],
  templateUrl: './mobile-business-view.component.html',
  styleUrl: './mobile-business-view.component.css',
})
export class MobileBusinessViewComponent implements OnInit, OnDestroy, OnChanges {
  @Input() activeTab = 'details';
  @Input() isFirstLogin = false;
  @Input() setupCompleted = false;
  welcomeDismissed = false;

  // Details tab inputs
  @Input() businessName = '';
  @Input() location = '';
  @Input() businessBrief = '';
  @Input() industry: string | null = null;
  @Input() businessType: string | null = null;
  @Input() vendorType = '';
  @Input() targetMarket: string[] = [];
  @Input() salesChannel: string[] = [];
  @Input() keyChallenges = '';

  // Dropdown options
  @Input() industries: Option[] = [];
  @Input() filteredBusinessTypes: Option[] = [];
  @Input() targetMarkets: Option[] = [];
  @Input() vendorTypes: Option[] = [];
  @Input() salesChannels: Option[] = [];

  // Schedule tab inputs
  @Input() operatingHours: any[] = [];
  @Input() daysOfWeek: { name: string; value: number }[] = [];
  @Input() allDaysOpenTime: string | null = null;
  @Input() allDaysCloseTime: string | null = null;
  @Input() exceptions: any[] = [];

  // Expenses tab inputs
  @Input() variableOverheadItems: VariableOverheadItem[] = [];
  @Input() fixedOverheadItems: FixedOverheadItem[] = [];
  @Input() employeeSalaries: Salary[] = [];

  // Employees tab inputs
  @Input() employees: Employee[] = [];
  @Input() roles: Role[] = [];

  // Extra data needed by offcanvas dropdowns
  @Input() periodTypes: any[] = [];
  @Input() variableOverheads: { value: string; label: string }[] = [];
  @Input() fixedOverheads: { value: string; label: string }[] = [];
  @Input() unitsOfMeasure: any[] = [];

  // Outputs — details two-way bindings
  @Output() businessNameChange = new EventEmitter<string>();
  @Output() locationChange = new EventEmitter<string>();
  @Output() businessBriefChange = new EventEmitter<string>();
  @Output() industryChange = new EventEmitter<string | null>();
  @Output() businessTypeChange = new EventEmitter<string | null>();
  @Output() vendorTypeChange = new EventEmitter<string | null>();
  @Output() targetMarketChange = new EventEmitter<string[]>();
  @Output() salesChannelChange = new EventEmitter<string[]>();
  @Output() keyChallengesChange = new EventEmitter<string>();

  // Schedule outputs
  @Output() allDaysOpenTimeChange = new EventEmitter<string | null>();
  @Output() allDaysCloseTimeChange = new EventEmitter<string | null>();
  @Output() applyToAllDays = new EventEmitter<void>();
  @Output() applyAllDays247 = new EventEmitter<void>();
  @Output() toggleDay24HoursEvent = new EventEmitter<number>();
  @Output() addException = new EventEmitter<void>();
  @Output() removeException = new EventEmitter<number>();

  // Variable overhead outputs
  @Output() addVariableOverhead = new EventEmitter<void>();
  @Output() saveVariableOverheadItem = new EventEmitter<VariableOverheadItem>();
  @Output() cancelVariableOverheadAdd = new EventEmitter<VariableOverheadItem>();
  @Output() deleteVariableOverhead = new EventEmitter<number>();

  // Fixed overhead outputs
  @Output() addFixedOverhead = new EventEmitter<void>();
  @Output() saveFixedOverheadItem = new EventEmitter<FixedOverheadItem>();
  @Output() cancelFixedOverheadAdd = new EventEmitter<FixedOverheadItem>();
  @Output() deleteFixedOverhead = new EventEmitter<number>();

  // Salary outputs
  @Output() addSalary = new EventEmitter<void>();
  @Output() saveSalaryItem = new EventEmitter<Salary>();
  @Output() cancelSalaryAdd = new EventEmitter<Salary>();
  @Output() deleteSalaryItem = new EventEmitter<number>();

  // Employee outputs
  @Output() addEmployee = new EventEmitter<void>();
  @Output() saveEmployeeItem = new EventEmitter<Employee>();
  @Output() cancelEmployeeAdd = new EventEmitter<Employee>();
  @Output() deleteEmployeeItem = new EventEmitter<number>();

  // Global submit — action: 'save' for per-tab saves, 'finish' for final setup completion
  @Output() submit = new EventEmitter<{ action: 'save' | 'finish' }>();

  // Guided setup tab order
  readonly setupTabs = ['details', 'schedule', 'expenses', 'employees', 'salaries'];

  get currentTabIndex(): number {
    return this.setupTabs.indexOf(this.activeTab);
  }

  get isLastSetupTab(): boolean {
    return this.activeTab === 'salaries';
  }

  get saveButtonLabel(): string {
    if (!this.isFirstLogin) return 'Save Changes';
    return this.isLastSetupTab ? 'Finish Setup' : 'Save & Continue';
  }

  get progressLabel(): string {
    const idx = this.currentTabIndex;
    if (idx < 0) return '';
    const labels: Record<string, string> = {
      details: 'Details', schedule: 'Schedule', expenses: 'Expenses',
      employees: 'Employees', salaries: 'Salaries',
    };
    return `Step ${idx + 1} of ${this.setupTabs.length} — ${labels[this.activeTab] ?? this.activeTab}`;
  }

  saveAndContinue(): void {
    if (this.isFirstLogin && this.isLastSetupTab) {
      this.submit.emit({ action: 'finish' });
    } else {
      this.submit.emit({ action: 'save' });
      if (this.isFirstLogin) {
        const nextTab = this.setupTabs[this.currentTabIndex + 1];
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { tab: nextTab },
          queryParamsHandling: '',
        });
      }
    }
  }

  expensesTab: 'variable' | 'fixed' = 'variable';

  // Per-tab search state
  private _searchVariable = '';
  get searchVariable() { return this._searchVariable; }
  set searchVariable(v: string) { this._searchVariable = v; this.variableCurrentPage = 1; }

  private _searchFixed = '';
  get searchFixed() { return this._searchFixed; }
  set searchFixed(v: string) { this._searchFixed = v; this.fixedCurrentPage = 1; }

  private _searchEmployees = '';
  get searchEmployees() { return this._searchEmployees; }
  set searchEmployees(v: string) { this._searchEmployees = v; this.employeesCurrentPage = 1; }

  private _searchSalaries = '';
  get searchSalaries() { return this._searchSalaries; }
  set searchSalaries(v: string) { this._searchSalaries = v; this.salariesCurrentPage = 1; }

  readonly pageSize = 10;
  variableCurrentPage = 1;
  fixedCurrentPage = 1;
  employeesCurrentPage = 1;
  salariesCurrentPage = 1;

  private matchesQuery(haystack: string, query: string): boolean {
    if (!query) return true;
    return haystack.toLowerCase().includes(query.trim().toLowerCase());
  }

  get filteredVariableOverheadItems() {
    const q = this.searchVariable;
    return (this.variableOverheadItems ?? []).filter(item => {
      const name = item.overheadName || item.customOverhead || item.overhead || '';
      const uom  = this.getUomCode(item.uomID);
      return this.matchesQuery(`${name} ${uom}`, q);
    });
  }

  get filteredFixedOverheadItems() {
    const q = this.searchFixed;
    return (this.fixedOverheadItems ?? []).filter(item => {
      const name   = item.customOverhead || item.overhead || '';
      const period = this.getPeriodName(item.periodTypeID);
      return this.matchesQuery(`${name} ${period}`, q);
    });
  }

  get filteredEmployees() {
    const q = this.searchEmployees;
    return (this.employees ?? []).filter(e => {
      const name = `${e.firstName ?? ''} ${e.lastName ?? ''}`;
      const role = e.roleDescription ?? '';
      return this.matchesQuery(`${name} ${role}`, q);
    });
  }

  get filteredSalaries() {
    const q = this.searchSalaries;
    return (this.employeeSalaries ?? []).filter(sal => {
      const name   = this.getEmployeeName(sal.employeeID);
      const period = this.getPeriodName(sal.periodTypeID);
      return this.matchesQuery(`${name} ${period}`, q);
    });
  }

  // Offcanvas state
  offcanvasVisible = false;
  offcanvasMode: BusinessOffcanvasMode = 'variable';
  offcanvasItem: any = null;

  // Setup complete modal state
  showSetupCompleteModal = false;

  // Delete confirm state
  showDeleteConfirm = false;
  private pendingDeleteFn: (() => void) | null = null;

  private subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private mobileNavService: MobileNavService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['setupCompleted']?.currentValue === true) {
      this.showSetupCompleteModal = true;
    }
  }

  ngOnInit(): void {
    this.subs.push(
      this.route.queryParams.subscribe(params => {
        if (params['tab']) {
          this.activeTab = params['tab'];
          this.variableCurrentPage = this.fixedCurrentPage = this.employeesCurrentPage = this.salariesCurrentPage = 1;
        }
      }),
      this.mobileNavService.activeTab.subscribe(tab => {
        if (tab === 'variable' || tab === 'fixed') {
          this.expensesTab = tab;
          this.variableCurrentPage = this.fixedCurrentPage = 1;
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  // ── Offcanvas open helpers ─────────────────────────────────────────────────

  openOffcanvasNewVariable(): void {
    if (window.innerWidth >= 768) return;
    const item = this.variableOverheadItems[0];
    if (item?.isNew) this.openOffcanvasForVariable(item);
  }

  openOffcanvasNewFixed(): void {
    if (window.innerWidth >= 768) return;
    const item = this.fixedOverheadItems[0];
    if (item?.isNew) this.openOffcanvasForFixed(item);
  }

  openOffcanvasNewSalary(): void {
    if (window.innerWidth >= 768) return;
    const item = this.employeeSalaries[0];
    if (item?.isNew) this.openOffcanvasForSalary(item);
  }

  openOffcanvasNewEmployee(): void {
    if (window.innerWidth >= 768) return;
    const item = this.employees[0] as any;
    if (item?.isNew) this.openOffcanvasForEmployee(item);
  }

  openOffcanvasForVariable(item: VariableOverheadItem): void {
    this.offcanvasMode = 'variable';
    this.offcanvasItem = item;
    this.offcanvasVisible = true;
  }

  openOffcanvasForFixed(item: FixedOverheadItem): void {
    this.offcanvasMode = 'fixed';
    this.offcanvasItem = item;
    this.offcanvasVisible = true;
  }

  openOffcanvasForSalary(item: Salary): void {
    this.offcanvasMode = 'salary';
    this.offcanvasItem = item;
    this.offcanvasVisible = true;
  }

  openOffcanvasForEmployee(item: Employee): void {
    this.offcanvasMode = 'employee';
    this.offcanvasItem = item;
    this.offcanvasVisible = true;
  }

  // ── Offcanvas save / cancel ────────────────────────────────────────────────

  onOffcanvasSave(editedData: any): void {
    if (!this.offcanvasItem) return;
    Object.assign(this.offcanvasItem, editedData);
    if (!this.offcanvasItem.isNew) {
      this.offcanvasItem.isModified = true;
    }
    switch (this.offcanvasMode) {
      case 'variable':  this.saveVariableOverheadItem.emit(this.offcanvasItem); break;
      case 'fixed':     this.saveFixedOverheadItem.emit(this.offcanvasItem);    break;
      case 'salary':    this.saveSalaryItem.emit(this.offcanvasItem);           break;
      case 'employee':  this.saveEmployeeItem.emit(this.offcanvasItem);         break;
    }
    this.closeOffcanvas();
  }

  onOffcanvasCancel(): void {
    if (this.offcanvasItem?.isNew) {
      switch (this.offcanvasMode) {
        case 'variable':  this.cancelVariableOverheadAdd.emit(this.offcanvasItem); break;
        case 'fixed':     this.cancelFixedOverheadAdd.emit(this.offcanvasItem);    break;
        case 'salary':    this.cancelSalaryAdd.emit(this.offcanvasItem);           break;
        case 'employee':  this.cancelEmployeeAdd.emit(this.offcanvasItem);         break;
      }
    }
    this.closeOffcanvas();
  }

  private closeOffcanvas(): void {
    this.offcanvasVisible = false;
    this.offcanvasItem = null;
  }

  // ── Delete confirm ─────────────────────────────────────────────────────────

  requestDelete(payload: any, type: 'variable' | 'fixed' | 'salary' | 'employee' | 'exception'): void {
    this.pendingDeleteFn = () => {
      switch (type) {
        case 'variable': {
          const index = this.variableOverheadItems.indexOf(payload);
          if (index >= 0) this.deleteVariableOverhead.emit(index);
          break;
        }
        case 'fixed': {
          const index = this.fixedOverheadItems.indexOf(payload);
          if (index >= 0) this.deleteFixedOverhead.emit(index);
          break;
        }
        case 'salary':
          if (payload?.salaryID != null) this.deleteSalaryItem.emit(payload.salaryID as number);
          break;
        case 'employee':
          if (payload?.employeeID != null) this.deleteEmployeeItem.emit(payload.employeeID as number);
          break;
        case 'exception':
          this.removeException.emit(payload as number);
          break;
      }
    };
    this.showDeleteConfirm = true;
  }

  confirmDelete(): void {
    this.showDeleteConfirm = false;
    if (this.pendingDeleteFn) {
      this.pendingDeleteFn();
      this.pendingDeleteFn = null;
    }
  }

  cancelDelete(): void {
    this.showDeleteConfirm = false;
    this.pendingDeleteFn = null;
  }

  // ── Utilities ──────────────────────────────────────────────────────────────

  expandedDays: Record<number, boolean> = {};

  toggleDay(i: number, ev: Event) {
    if ((ev.target as HTMLElement).closest('.form-switch')) return;
    this.expandedDays[i] = !this.expandedDays[i];
  }

  formatDayTimes(day: any): string {
    if (day.isClosed) return 'Closed';
    if (day.openTime === '00:00' && day.closeTime === '23:59') return '24 Hrs';
    return `${day.openTime ?? '--:--'} – ${day.closeTime ?? '--:--'}`;
  }

  /** Returns true when a day is configured as open for the full 24 hours. */
  isDayOpen24Hours(day: any): boolean {
    return day.openTime === '00:00' && day.closeTime === '23:59' && !day.isClosed;
  }

  getDayName(dayOfWeek: number): string {
    const day = this.daysOfWeek.find(d => d.value === dayOfWeek);
    return day?.name ?? `Day ${dayOfWeek}`;
  }

  getUomCode(uomID: number | null | undefined): string {
    if (uomID == null) return 'N/A';
    return (this.unitsOfMeasure.find((u: any) => u.uomID === uomID)?.uomCode ?? 'N/A').replace(/2$/, '²');
  }

  variableOverheadFields(item: VariableOverheadItem): MobileCardField[] {
    return [
      { label: 'Purchase Price', value: `R ${(item.purchasePrice ?? 0).toFixed(2)}`, chip: true },
      { label: 'Qty', value: item.purchaseQuantity ?? 0, chip: true },
      { label: 'UOM', value: this.getUomCode(item.uomID), chip: true },
    ];
  }

  getPeriodName(id: number | null | undefined): string {
    if (id == null) return 'N/A';
    return (this.periodTypes as any[]).find(p => p.periodTypeID === id)?.description ?? 'N/A';
  }

  getEmployeeName(id: number | null | undefined): string {
    if (id == null) return '—';
    const emp = this.employees.find(e => e.employeeID === id);
    return emp ? `${emp.firstName} ${emp.lastName}` : `Employee #${id}`;
  }

  fixedOverheadFields(item: FixedOverheadItem): MobileCardField[] {
    return [
      { label: 'Cost', value: `R ${(item.cost ?? 0).toFixed(2)}`, chip: true },
      { label: 'Period', value: this.getPeriodName(item.periodTypeID), chip: true },
    ];
  }

  salaryFields(item: Salary): MobileCardField[] {
    return [
      { label: 'Amount', value: `R ${(item.amount ?? 0).toFixed(2)}`, chip: true },
      { label: 'Payment Rate Type', value: this.getPeriodName(item.periodTypeID), chip: true },
      { label: 'Hours Worked', value: item.hoursWorked != null ? (item.hoursWorked as number).toFixed(2) : 'N/A' },
    ];
  }

  employeeFields(emp: Employee): MobileCardField[] {
    return [
      { label: 'Role', value: emp.roleDescription ?? 'N/A', chip: true },
    ];
  }

  exceptionFields(ex: any): MobileCardField[] {
    return [
      { label: 'Type', value: ex.isClosed ? 'Closed' : 'Custom Hours', chip: true, warn: ex.isClosed },
      { label: 'Hours', value: ex.isClosed ? '—' : `${ex.openTime ?? '?'} – ${ex.closeTime ?? '?'}` },
      { label: 'Reason', value: ex.reason ?? '' },
    ];
  }

  // ── Pagination ─────────────────────────────────────────────────────────────
  get filteredVariableOverheadTotalPages(): number { return Math.max(1, Math.ceil(this.filteredVariableOverheadItems.length / this.pageSize)); }
  get filteredVariableOverheadPaged() { const s = (this.variableCurrentPage - 1) * this.pageSize; return this.filteredVariableOverheadItems.slice(s, s + this.pageSize); }
  changeVariablePage(page: number): void { if (page >= 1 && page <= this.filteredVariableOverheadTotalPages) this.variableCurrentPage = page; }

  get filteredFixedOverheadTotalPages(): number { return Math.max(1, Math.ceil(this.filteredFixedOverheadItems.length / this.pageSize)); }
  get filteredFixedOverheadPaged() { const s = (this.fixedCurrentPage - 1) * this.pageSize; return this.filteredFixedOverheadItems.slice(s, s + this.pageSize); }
  changeFixedPage(page: number): void { if (page >= 1 && page <= this.filteredFixedOverheadTotalPages) this.fixedCurrentPage = page; }

  get filteredSalariesTotalPages(): number { return Math.max(1, Math.ceil(this.filteredSalaries.length / this.pageSize)); }
  get filteredSalariesPaged() { const s = (this.salariesCurrentPage - 1) * this.pageSize; return this.filteredSalaries.slice(s, s + this.pageSize); }
  changeSalariesPage(page: number): void { if (page >= 1 && page <= this.filteredSalariesTotalPages) this.salariesCurrentPage = page; }

  get filteredEmployeesTotalPages(): number { return Math.max(1, Math.ceil(this.filteredEmployees.length / this.pageSize)); }
  get filteredEmployeesPaged() { const s = (this.employeesCurrentPage - 1) * this.pageSize; return this.filteredEmployees.slice(s, s + this.pageSize); }
  changeEmployeesPage(page: number): void { if (page >= 1 && page <= this.filteredEmployeesTotalPages) this.employeesCurrentPage = page; }
}
